import React from 'react';

// Ancestor fan chart: the starting person at the centre, each generation a ring of
// wedges above them, father's side on the left and mother's on the right.

export interface FanDatum {
  id: string;
  name: string;
  attributes?: { gender?: string; dob?: string; is_ghost?: boolean };
  children?: FanDatum[];
  __linkType?: string;
}

interface FanChartProps {
  root: FanDatum;
  zoom: number;
  fillFor: (id: string) => string;
  ghostConfidence: (id: string) => number | null;
  onSelect: (datum: FanDatum) => void;
}

interface Wedge {
  datum: FanDatum;
  depth: number;
  start: number;
  end: number;
}

const CENTRE_RADIUS = 70;
// Inner rings are wide arcs with text along them; outer rings are thin wedges
// with text running outwards, so they need more depth than width
const ringWidth = (depth: number) => (depth <= 3 ? 80 : 115);
const innerRadius = (depth: number) => {
  let r = CENTRE_RADIUS;
  for (let d = 1; d < depth; d += 1) r += ringWidth(d);
  return r;
};

function isFemale(d: FanDatum) {
  const g = (d.attributes?.gender || '').toLowerCase();
  return g === 'female' || g === 'f';
}

function layout(root: FanDatum): Wedge[] {
  const wedges: Wedge[] = [];
  const place = (node: FanDatum, depth: number, start: number, end: number) => {
    // Confirmed parents before potential ones; father (left) before mother (right)
    const parents = [...(node.children || [])].sort(
      (a, b) =>
        Number(a.__linkType === 'potential') - Number(b.__linkType === 'potential') ||
        Number(isFemale(a)) - Number(isFemale(b))
    );
    const share = (end - start) / Math.max(parents.length, 1);
    parents.forEach((parent, i) => {
      const s = start + i * share;
      wedges.push({ datum: parent, depth: depth + 1, start: s, end: s + share });
      place(parent, depth + 1, s, s + share);
    });
  };
  // Upper semicircle: π (left) to 2π (right); SVG y points down, so sin < 0 is up
  place(root, 0, Math.PI, 2 * Math.PI);
  return wedges;
}

const point = (r: number, a: number) => `${(r * Math.cos(a)).toFixed(2)},${(r * Math.sin(a)).toFixed(2)}`;

function wedgePath(r0: number, r1: number, start: number, end: number) {
  const large = end - start > Math.PI ? 1 : 0;
  return [
    `M${point(r1, start)}`,
    `A${r1},${r1} 0 ${large} 1 ${point(r1, end)}`,
    `L${point(r0, end)}`,
    `A${r0},${r0} 0 ${large} 0 ${point(r0, start)}`,
    'Z',
  ].join(' ');
}

const birthYear = (dob?: string) => (dob ? (dob.match(/\b(1[0-9]{3}|20[0-9]{2})\b/) || [])[1] : undefined);

function fit(text: string, maxChars: number) {
  if (maxChars < 2) return '';
  return text.length <= maxChars ? text : `${text.slice(0, Math.max(1, maxChars - 1))}…`;
}

const FanChart: React.FC<FanChartProps> = ({ root, zoom, fillFor, ghostConfidence, onSelect }) => {
  const wedges = layout(root);
  const maxDepth = wedges.reduce((m, w) => Math.max(m, w.depth), 0);
  const radius = innerRadius(maxDepth + 1);
  const pad = 12;
  const width = 2 * (radius + pad);
  const height = radius + CENTRE_RADIUS + 2 * pad;

  const renderLabel = (w: Wedge, r0: number, r1: number) => {
    const mid = (w.start + w.end) / 2;
    const rm = (r0 + r1) / 2;
    const fontSize = Math.max(9, 14 - w.depth);
    const year = birthYear(w.datum.attributes?.dob);
    const [x, y] = [rm * Math.cos(mid), rm * Math.sin(mid)];
    const deg = (mid * 180) / Math.PI;
    // Text follows the arc when the wedge is wide, otherwise runs outwards
    const along = rm * (w.end - w.start) > r1 - r0;
    const length = along ? rm * (w.end - w.start) - 8 : r1 - r0 - 10;
    const thickness = along ? r1 - r0 : rm * (w.end - w.start);
    const rotate = along ? deg + 90 : deg < 270 ? deg + 180 : deg;
    const maxChars = Math.floor(length / (fontSize * 0.58));
    const twoLines = thickness >= fontSize * 2.6;
    return (
      <text
        transform={`translate(${x.toFixed(2)},${y.toFixed(2)}) rotate(${rotate.toFixed(2)})`}
        textAnchor="middle"
        fontSize={fontSize}
        className="fan-chart-label"
      >
        <tspan x={0} dy={twoLines ? -fontSize * 0.2 : fontSize * 0.35}>{fit(w.datum.name, maxChars)}</tspan>
        {twoLines && year && <tspan x={0} dy={fontSize * 1.1} className="fan-chart-year">{year}</tspan>}
      </text>
    );
  };

  return (
    <svg
      className="fan-chart"
      viewBox={`${-width / 2} ${-(radius + pad)} ${width} ${height}`}
      width={width * zoom}
      height={height * zoom}
      role="img"
      aria-label={`Fan chart of ${root.name}'s ancestors`}
    >
      {wedges.map(w => {
        const r0 = innerRadius(w.depth);
        const r1 = r0 + ringWidth(w.depth);
        const confidence = w.datum.attributes?.is_ghost ? ghostConfidence(w.datum.id) : null;
        const classes = ['fan-chart-wedge'];
        if (confidence !== null) classes.push('is-ghost');
        if (w.datum.__linkType === 'potential') classes.push('is-potential');
        return (
          <g
            key={`${w.datum.id}-${w.start}`}
            className={classes.join(' ')}
            onClick={() => onSelect(w.datum)}
            style={confidence !== null ? { opacity: 0.45 + 0.55 * confidence } : undefined}
          >
            <title>
              {w.datum.name}
              {w.datum.attributes?.dob ? ` (b. ${w.datum.attributes.dob})` : ''}
              {confidence !== null ? ` · ghost, ${Math.round(confidence * 100)}% confidence` : ''}
              {w.datum.__linkType === 'potential' ? ' · potential parent' : ''}
            </title>
            <path d={wedgePath(r0, r1, w.start, w.end)} fill={fillFor(w.datum.id)} />
            {renderLabel(w, r0, r1)}
          </g>
        );
      })}
      <g className="fan-chart-wedge fan-chart-centre" onClick={() => onSelect(root)}>
        <title>{root.name}</title>
        <circle r={CENTRE_RADIUS} fill={fillFor(root.id)} />
        <text textAnchor="middle" fontSize={13} className="fan-chart-label">
          {root.name.split(' ').slice(0, 3).map((part, i, parts) => (
            <tspan key={i} x={0} dy={i === 0 ? -((parts.length - 1) * 8) + 4 : 16}>{part}</tspan>
          ))}
        </text>
      </g>
    </svg>
  );
};

export default FanChart;
